import { useQuery } from "@tanstack/react-query";
import React from "react";
import { Page } from "../../../activities/AppPage";
import { API } from "../../../API";
import useShare from "../../../hooks/useShare";
import useTrainingCenterState from "../../../hooks/useTrainingCenterState";
import { isLoading } from "../../../Loading";
import {
  AgencyId,
  CaregiverComplianceItemMobile,
  CaregiverComplianceReferenceDocumentData,
  CaregiverDocumentTypeId,
  ComplianceStatus,
  TrainingCenterBundleDueDateId,
} from "../../../messages";
import { formatPhone } from "../../../phone_number";
import { STRINGS } from "../../../strings";
import { AppState } from "../../../system/AppState";
import { translate } from "../../../translate";
import { useModalV2 } from "../../AppModals";
import ErrorScaffold from "../../ErrorScaffold";
import AppModal from "../../Generic/AppModal/AppModal";
import { TrainingCenterLanguagePreference } from "../../TrainingCenter/TrainingCenterLanguageLookup";
import useModal from "../../useModal";
import { withMedflytAgencyId } from "../../withMedflytAgencyId";
import MyMedflytComplianceScreen from "./MyMedflytComplianceScreen";

interface Props {
  api: API;
  medflytAgencyId: AgencyId;
  appState: AppState;
  languagePreference: TrainingCenterLanguagePreference;
  onRequestNavigate: (page: Page) => void;
  onClickGoBack: () => void;
}

function MyMedflytComplianceActivity(props: Props) {
  const query = useQuery({
    queryKey: ["compliance_items", props.medflytAgencyId],
    queryFn: () => props.api.getComplianceItems(props.medflytAgencyId),
  });
  const modal = useModal();
  const modals = useModalV2();
  const { share } = useShare();
  const trainingCenterState = useTrainingCenterState(props.appState);
  const bundles = React.useMemo(() => {
    return isLoading(trainingCenterState)
      ? []
      : trainingCenterState.bundles.bundles
          .filter((bundle) => bundle.agencyId === props.medflytAgencyId)
          .toArray();
  }, [props.medflytAgencyId, trainingCenterState]);

  const handleRedirectToItemOverviewPage = (item: CaregiverComplianceItemMobile) => {
    props.onRequestNavigate({ type: "ComplianceItem", documentTypeId: item.documentType.id });
  };

  const handleClickShare = (shareUrl: string | null) => {
    if (shareUrl !== null) {
      share({
        text: translate(STRINGS.HI_PLEASE_HELP_ME_FILL_MY_APPLICATION),
        url: shareUrl,
      });
    }
    modal.setModalAsClosed();
  };

  const handleClickComplianceItem = (item: CaregiverComplianceItemMobile) => {
    if (item.documentType.adhocType === "i-9") {
      props.onRequestNavigate({ type: "ComplianceI9" });
      return;
    }

    if (
      item.documentType.adhocType === "flu" &&
      item.documentType.fillableUploadAlternative !== null
    ) {
      modals.createModal({
        type: "neutral",
        title: translate(STRINGS.FLU_SHOT),
        message: translate(STRINGS.FLU_SHOT_SUBMITTION_MESSAGE),
        actions: [
          ({ close }) => ({
            key: "sign",
            variant: "filled",
            children: translate(STRINGS.SIGN_DECLINATION_FORM),
            onClick: () => {
              props.onRequestNavigate({
                type: "ComplianceDocumentForm",
                documentTypeId: item.documentType.id,
              });
              close();
            },
          }),
          ({ close }) => ({
            key: "upload",
            variant: "link",
            children: translate(STRINGS.UPLOAD_FLU_SHOT_DOCUMENT),
            onClick: () => {
              const documentTypeId = item.documentType.fillableUploadAlternative;

              if (documentTypeId === null) {
                return;
              }

              props.onRequestNavigate({ type: "ComplianceItem", documentTypeId });
              close();
            },
          }),
        ],
      });
      return;
    }

    if (item.documentType.isFillable) {
      props.onRequestNavigate({
        type: "ComplianceDocumentForm",
        documentTypeId: item.documentType.id,
      });
      return;
    }

    handleRedirectToItemOverviewPage(item);
  };

  const handleClickReferenceItem = (
    documentTypeId: CaregiverDocumentTypeId,
    referenceData: CaregiverComplianceReferenceDocumentData | null,
    complianceStatus: ComplianceStatus
  ) => {
    if (complianceStatus === "Compliant" || referenceData === null) {
      return;
    }
    if (referenceData.referenceName !== null && referenceData.referencePhoneNumber !== null) {
      return modal.setModalAsPrompt({
        title: translate(STRINGS.REQUESTING_REFERENCE_RESPONSE),
        message: translate(STRINGS.STILL_WAITING_REFERENCE_ANSWER)
          .replace("{REFERENCE_NAME}", `\n${referenceData.referenceName}`)
          .replace(
            "{REFERENCE_PHONE_NUMBER}",
            `${formatPhone(referenceData.referencePhoneNumber)}\n`
          ),
        withoutClose: true,
        actions: [
          {
            key: "ok",
            variant: "filled",
            children:
              referenceData.referenceLink !== null
                ? translate(STRINGS.SHARE_REFERENCE_LINK)
                : translate(STRINGS.OK),
            onClick: () => handleClickShare(referenceData.referenceLink),
          },
        ],
      });
    }
    props.onRequestNavigate({
      type: "ComplianceDocumentForm",
      documentTypeId,
    });
  };

  const handleClickVerifiedItems = () => {
    props.onRequestNavigate({
      type: "CompliantItems",
      onClickComplianceItem: handleRedirectToItemOverviewPage,
      onClickReferenceItem: handleClickReferenceItem,
    });
  };

  const handleClickBundle = (bundleDueDateId: TrainingCenterBundleDueDateId) => {
    props.onRequestNavigate({
      type: "TrainingCenterBundle",
      bundleDueDateId: bundleDueDateId,
    });
  };

  switch (query.status) {
    case "error":
      return (
        <ErrorScaffold
          title={translate(STRINGS.ONBOARDING)}
          message={translate(STRINGS.SOMETHING_WENT_WRONG)}
          onClickRetry={query.refetch}
          onClickGoBack={props.onClickGoBack}
        />
      );

    case "loading":
      return <MyMedflytComplianceScreen isLoading={true} onClickGoBack={props.onClickGoBack} />;

    case "success": {
      if (isLoading(trainingCenterState)) {
        return <MyMedflytComplianceScreen isLoading={true} onClickGoBack={props.onClickGoBack} />;
      }

      return (
        <>
          <MyMedflytComplianceScreen
            isLoading={false}
            languagePreference={props.languagePreference}
            items={query.data.items}
            bundles={bundles}
            onClickComplianceItem={handleClickComplianceItem}
            onClickReferenceItem={handleClickReferenceItem}
            onClickVerifiedItems={handleClickVerifiedItems}
            onClickBundle={handleClickBundle}
            onClickGoBack={props.onClickGoBack}
          />
          <AppModal {...modal.modalProps} />
        </>
      );
    }
  }
}

export default withMedflytAgencyId(MyMedflytComplianceActivity);
